import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faChevronLeft,
  faChevronRight,
  faBuilding,
  faClockRotateLeft,
  faCircleCheck,
  faCircleExclamation,
  faFileCirclePlus,
  faGear,
  faLink,
  faPenToSquare,
  faReceipt,
  faRotate,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import InputField from "../../../components/InputField/InputField"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { useError } from "../../../context/useErrorContext"
import { useMessage } from "../../../context/useMessageContext"
import { useQuickActionPopup } from "../../../hooks/useQuickActionPopup"
import {
  defaultFiscalIntegrationSettings,
  useFiscalIntegration,
} from "../../../hooks/FiscalIntegration/useFiscalIntegration"
import { EFiscalEnvironment } from "../../../Objects/FiscalIntegration/EFiscalEnvironment"
import { EFiscalDocumentStatus } from "../../../Objects/FiscalIntegration/EFiscalDocumentStatus"
import { type ISaveFiscalIntegrationSettings } from "../../../Objects/FiscalIntegration/ISaveFiscalIntegrationSettings"
import { type IFiscalProductProfile } from "../../../Objects/FiscalIntegration/IFiscalProductProfile"
import { type IFiscalDocument } from "../../../Objects/FiscalIntegration/IFiscalDocument"
import {
  type FiscalDocumentSearchResult,
  type IFiscalDocumentFilter,
} from "../../../Objects/FiscalIntegration/IFiscalDocumentFilter"
import { type ISaveFiscalIssuerSettings } from "../../../Objects/FiscalIntegration/IFiscalIssuerSettings"
import { FiscalSettingsPopup } from "./components/FiscalSettingsPopup"
import { FiscalIssuerSettingsPopup } from "./components/FiscalIssuerSettingsPopup"
import { FiscalProductProfilePopup } from "./components/FiscalProductProfilePopup"
import { FiscalCancelPopup } from "./components/FiscalCancelPopup"
import { FiscalManualEmissionPopup } from "./components/FiscalManualEmissionPopup"
import { FiscalDocumentsPopup } from "./components/FiscalDocumentsPopup"
import { FiscalDocumentPrintPopup } from "../../../components/FiscalIntegration/FiscalDocumentPrintPopup"
import { EPaymentType } from "../../../Enum/EPaymentType"

const PRODUCTS_PAGE_SIZE = 30
const DOCUMENTS_PAGE_SIZE = 20
const DOCUMENTS_POLLING_INTERVAL_MS = 5000

const initialDocumentSearchResult: FiscalDocumentSearchResult = {
  items: [],
  pageNumber: 1,
  pageSize: DOCUMENTS_PAGE_SIZE,
  totalCount: 0,
  hasNextPage: false,
}

const toEditableSettings = (settings = defaultFiscalIntegrationSettings): ISaveFiscalIntegrationSettings => ({
  ...settings,
  homologationToken: "",
  productionToken: "",
  clearHomologationToken: false,
  clearProductionToken: false,
})

const toEditableIssuer = (settings = defaultFiscalIntegrationSettings): ISaveFiscalIssuerSettings => ({
  cnpj: settings.issuer.cnpj,
  legalName: settings.issuer.legalName,
  tradeName: settings.issuer.tradeName,
  stateRegistration: settings.issuer.stateRegistration,
  street: settings.issuer.street,
  number: settings.issuer.number,
  complement: settings.issuer.complement,
  neighborhood: settings.issuer.neighborhood,
  city: settings.issuer.city,
  state: settings.issuer.state,
  zipCode: settings.issuer.zipCode,
})

export const FiscalIntegrationPage: React.FC = () => {
  const { t } = useTranslation(["fiscalIntegration", "helpPopup"])

  const fiscal = useFiscalIntegration()

  const fiscalRef = useRef(fiscal)

  const { setError } = useError()

  const { setMessage } = useMessage()

  const [settings, setSettings] = useState(defaultFiscalIntegrationSettings)

  const [settingsDraft, setSettingsDraft] = useState<ISaveFiscalIntegrationSettings>(toEditableSettings())

  const [issuerDraft, setIssuerDraft] = useState<ISaveFiscalIssuerSettings>(toEditableIssuer())

  const [products, setProducts] = useState<IFiscalProductProfile[]>([])

  const [documents, setDocuments] = useState<IFiscalDocument[]>([])

  const [documentSearchResult, setDocumentSearchResult] =
    useState<FiscalDocumentSearchResult>(initialDocumentSearchResult)

  const [search, setSearch] = useState("")

  const [currentPage, setCurrentPage] = useState(1)

  const [orderId, setOrderId] = useState("")

  const [consumerCpf, setConsumerCpf] = useState("")

  const [manualPaymentType, setManualPaymentType] = useState<EPaymentType>(EPaymentType.None)

  const [manualRequiresPaymentMethod, setManualRequiresPaymentMethod] = useState(false)

  const [isManualOrderChecking, setIsManualOrderChecking] = useState(false)

  const [selectedProductIds, setSelectedProductIds] = useState<Set<number>>(new Set())

  const [settingsOpen, setSettingsOpen] = useState(false)

  const [issuerOpen, setIssuerOpen] = useState(false)

  const [manualEmissionOpen, setManualEmissionOpen] = useState(false)

  const [documentsOpen, setDocumentsOpen] = useState(false)

  const [profileOpen, setProfileOpen] = useState(false)

  const [profileDraft, setProfileDraft] = useState<IFiscalProductProfile | null>(null)

  const [bulkMode, setBulkMode] = useState(false)

  const [cancelDocument, setCancelDocument] = useState<IFiscalDocument | null>(null)

  const [printDocument, setPrintDocument] = useState<IFiscalDocument | null>(null)

  const productsSectionRef = useRef<HTMLElement>(null)

  const productsListRef = useRef<HTMLDivElement>(null)

  const knownFailureSignaturesRef = useRef<Map<number, string>>(new Map())

  const isReloadingDocumentsRef = useRef(false)

  const setErrorRef = useRef(setError)

  const translationRef = useRef(t)

  useEffect(() => {
    fiscalRef.current = fiscal
  }, [fiscal])

  useEffect(() => {
    const parsedOrderId = Number(orderId)

    setManualRequiresPaymentMethod(false)
    setManualPaymentType(EPaymentType.None)

    if (!manualEmissionOpen || !Number.isInteger(parsedOrderId) || parsedOrderId <= 0) {
      setIsManualOrderChecking(false)
      return
    }

    let isActive = true
    setIsManualOrderChecking(true)

    const timeout = window.setTimeout(() => {
      void fiscalRef.current
        .getOrderEmissionStatus(parsedOrderId, false)
        .then((status) => {
          if (isActive) {
            setManualRequiresPaymentMethod(status.requiresPaymentMethod)
          }
        })
        .catch(() => undefined)
        .finally(() => {
          if (isActive) {
            setIsManualOrderChecking(false)
          }
        })
    }, 350)

    return () => {
      isActive = false
      window.clearTimeout(timeout)
    }
  }, [manualEmissionOpen, orderId])

  useEffect(() => {
    setErrorRef.current = setError
    translationRef.current = t
  }, [setError, t])

  const synchronizeDocuments = useCallback((nextDocuments: IFiscalDocument[], notifyNewFailures: boolean) => {
    const previousFailureSignatures = knownFailureSignaturesRef.current
    const nextFailureSignatures = new Map<number, string>()
    const newFailures: IFiscalDocument[] = []

    nextDocuments.forEach((document) => {
      if (document.status !== EFiscalDocumentStatus.Rejected) {
        return
      }

      const signature = `${document.updatedAt}:${document.message ?? ""}`
      nextFailureSignatures.set(document.id, signature)

      if (notifyNewFailures && previousFailureSignatures.get(document.id) !== signature) {
        newFailures.push(document)
      }
    })

    knownFailureSignaturesRef.current = nextFailureSignatures
    setDocuments((currentDocuments) => {
      const hasChanges =
        currentDocuments.length !== nextDocuments.length ||
        currentDocuments.some((currentDocument, index) => {
          const nextDocument = nextDocuments[index]
          return (
            !nextDocument ||
            currentDocument.id !== nextDocument.id ||
            currentDocument.status !== nextDocument.status ||
            currentDocument.updatedAt !== nextDocument.updatedAt ||
            currentDocument.message !== nextDocument.message
          )
        })

      return hasChanges ? nextDocuments : currentDocuments
    })

    const latestFailure = newFailures[0]

    if (latestFailure) {
      const translate = translationRef.current

      setErrorRef.current(
        translate("feedback.failureTitle"),
        translate("feedback.failureDetected", {
          orderId: latestFailure.orderEntityId,
          message: latestFailure.message ?? translate("documents.failureUnknown"),
        }),
      )
    }
  }, [])

  const load = useCallback(async () => {
    const [nextSettings, nextProducts, nextDocuments] = await Promise.all([
      fiscalRef.current.getSettings(),
      fiscalRef.current.getProducts(),
      fiscalRef.current.getDocuments(),
    ])
    setSettings(nextSettings)
    setSettingsDraft(toEditableSettings(nextSettings))
    setProducts(nextProducts)
    synchronizeDocuments(nextDocuments, false)
    setSelectedProductIds(new Set())
    setCurrentPage(1)
  }, [synchronizeDocuments])

  useEffect(() => {
    void load()
  }, [load])

  const openSettings = useCallback(() => {
    setSettingsDraft(toEditableSettings(settings))
    setSettingsOpen(true)
  }, [settings])

  const openIssuer = useCallback(() => {
    setIssuerDraft(toEditableIssuer(settings))
    setIssuerOpen(true)
  }, [settings])

  const openManualEmission = useCallback(() => setManualEmissionOpen(true), [])

  const reloadDocuments = useCallback(
    async (showFeedback = true, notifyNewFailures = true) => {
      if (isReloadingDocumentsRef.current) {
        return
      }

      isReloadingDocumentsRef.current = true

      try {
        synchronizeDocuments(await fiscalRef.current.getDocuments(showFeedback), notifyNewFailures)
      } finally {
        isReloadingDocumentsRef.current = false
      }
    },
    [synchronizeDocuments],
  )

  const searchDocuments = useCallback(async (filter: IFiscalDocumentFilter) => {
    const result = await fiscalRef.current.searchDocuments(filter)
    setDocumentSearchResult(result)
  }, [])

  const openDocuments = useCallback(() => {
    setDocumentsOpen(true)
  }, [])

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      if (document.hidden) {
        return
      }

      void reloadDocuments(false).catch(() => undefined)
    }, DOCUMENTS_POLLING_INTERVAL_MS)

    return () => window.clearInterval(intervalId)
  }, [reloadDocuments])

  const updateDocument = (updatedDocument: IFiscalDocument | null) => {
    if (!updatedDocument) {
      return
    }

    if (updatedDocument.status !== EFiscalDocumentStatus.Rejected) {
      knownFailureSignaturesRef.current.delete(updatedDocument.id)
    }

    setDocuments((current) => {
      const documentExists = current.some((document) => document.id === updatedDocument.id)
      return documentExists
        ? current.map((document) => (document.id === updatedDocument.id ? updatedDocument : document))
        : [updatedDocument, ...current]
    })

    setDocumentSearchResult((current) => ({
      ...current,
      items: current.items.map((document) =>
        document.id === updatedDocument.id ? updatedDocument : document,
      ),
    }))
  }

  useQuickActionPopup("fiscal-settings", openSettings)
  useQuickActionPopup("fiscal-issuer", openIssuer)
  useQuickActionPopup(
    "fiscal-products",
    useCallback(() => productsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), []),
  )
  useQuickActionPopup("fiscal-emit", openManualEmission)
  useQuickActionPopup("fiscal-documents", openDocuments)

  const filteredProducts = useMemo(() => {
    const normalized = search.trim().toLocaleLowerCase()
    return normalized
      ? products.filter((product) =>
          [product.productName, product.ncm, product.cfop, product.productCode]
            .join(" ")
            .toLocaleLowerCase()
            .includes(normalized),
        )
      : products
  }, [products, search])

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / PRODUCTS_PAGE_SIZE))

  const visiblePage = Math.min(currentPage, totalPages)

  const paginatedProducts = useMemo(() => {
    const startIndex = (visiblePage - 1) * PRODUCTS_PAGE_SIZE
    return filteredProducts.slice(startIndex, startIndex + PRODUCTS_PAGE_SIZE)
  }, [filteredProducts, visiblePage])

  const firstVisibleProduct = filteredProducts.length === 0 ? 0 : (visiblePage - 1) * PRODUCTS_PAGE_SIZE + 1

  const lastVisibleProduct = Math.min(visiblePage * PRODUCTS_PAGE_SIZE, filteredProducts.length)

  const configuredProducts = products.filter((product) => product.isConfigured).length

  const allProductsSelected = products.length > 0 && selectedProductIds.size === products.length

  const authorizedDocuments = documents.filter(
    (document) => document.status === EFiscalDocumentStatus.Authorized,
  ).length

  const rejectedDocuments = documents.filter((document) => document.status === EFiscalDocumentStatus.Rejected)

  const latestRejectedDocument = rejectedDocuments[0]

  const handleSaveSettings = async () => {
    const saved = await fiscal.saveSettings(settingsDraft)

    const updatedProducts = await fiscal.getProducts()

    setSettings(saved)

    setSettingsDraft(toEditableSettings(saved))

    setProducts(updatedProducts)

    setSettingsOpen(false)

    setMessage(t("feedback.successTitle"), t("feedback.settingsSaved"))
  }

  const handleSaveIssuer = async () => {
    const savedIssuer = await fiscal.saveIssuerSettings(issuerDraft)
    setSettings((current) => ({ ...current, issuer: savedIssuer }))
    setIssuerDraft({
      cnpj: savedIssuer.cnpj,
      legalName: savedIssuer.legalName,
      tradeName: savedIssuer.tradeName,
      stateRegistration: savedIssuer.stateRegistration,
      street: savedIssuer.street,
      number: savedIssuer.number,
      complement: savedIssuer.complement,
      neighborhood: savedIssuer.neighborhood,
      city: savedIssuer.city,
      state: savedIssuer.state,
      zipCode: savedIssuer.zipCode,
    })
    setIssuerOpen(false)
    setMessage(t("feedback.successTitle"), t("feedback.issuerSaved"))
  }

  const handleTest = async () => {
    const token =
      settingsDraft.environment === EFiscalEnvironment.Homologation
        ? settingsDraft.homologationToken
        : settingsDraft.productionToken
    const result = await fiscal.testConnection({ environment: settingsDraft.environment, token })
    setMessage(t("feedback.successTitle"), result.message || t("feedback.connectionValid"))
  }

  const openProduct = (product: IFiscalProductProfile) => {
    setBulkMode(false)

    setProfileDraft({ ...product })

    setProfileOpen(true)
  }

  const openBulk = () => {
    const template = products.find((product) => selectedProductIds.has(product.catalogProductEntityId))

    if (!template) {
      return
    }

    setBulkMode(true)

    setProfileDraft({ ...template })

    setProfileOpen(true)
  }

  const handleSaveProfile = async () => {
    if (!profileDraft) {
      return
    }

    if (bulkMode) {
      const updated = await fiscal.saveProductsBulk([...selectedProductIds], profileDraft)

      setProducts(updated)

      setSelectedProductIds(new Set())
    } else {
      const updated = await fiscal.saveProduct(profileDraft)
      setProducts((current) =>
        current.map((product) => (product.catalogProductEntityId === updated.catalogProductEntityId ? updated : product)),
      )
    }

    setProfileOpen(false)

    setMessage(t("feedback.successTitle"), t("feedback.productsSaved"))
  }

  const toggleProduct = (productId: number) => {
    setSelectedProductIds((current) => {
      const next = new Set(current)
      if (next.has(productId)) {
        next.delete(productId)
      } else {
        next.add(productId)
      }
      return next
    })
  }

  const toggleAllProducts = () => {
    setSelectedProductIds(
      allProductsSelected ? new Set() : new Set(products.map((product) => product.catalogProductEntityId)),
    )
  }

  const changePage = (page: number) => {
    setCurrentPage(Math.min(Math.max(page, 1), totalPages))
    productsListRef.current?.scrollTo({ top: 0, behavior: "smooth" })
  }

  const handleEmit = async () => {
    const parsedOrderId = Number(orderId)

    if (!Number.isInteger(parsedOrderId) || parsedOrderId <= 0) {
      return
    }

    if (manualRequiresPaymentMethod && manualPaymentType === EPaymentType.None) {
      return
    }

    const emittedDocument = await fiscal.emitOrder(
      parsedOrderId,
      consumerCpf,
      manualRequiresPaymentMethod ? manualPaymentType : undefined,
    )

    setOrderId("")
    setConsumerCpf("")
    setManualPaymentType(EPaymentType.None)
    setManualRequiresPaymentMethod(false)

    updateDocument(emittedDocument)

    setManualEmissionOpen(false)

    setDocumentsOpen(true)

    setMessage(t("feedback.successTitle"), t("feedback.emissionQueued"))
  }

  const handleRetry = async (documentId: number) => {
    updateDocument(await fiscal.retryDocument(documentId))

    setMessage(t("feedback.successTitle"), t("feedback.retryQueued"))
  }

  const handleRefresh = async (documentId: number) => {
    updateDocument(await fiscal.refreshDocument(documentId))

    setMessage(t("feedback.successTitle"), t("feedback.refreshQueued"))
  }

  const handleCancel = async (reason: string) => {
    if (!cancelDocument) {
      return
    }

    const canceledDocument = await fiscal.cancelDocument(cancelDocument.id, reason)

    setCancelDocument(null)

    updateDocument(canceledDocument)

    setMessage(t("feedback.successTitle"), t("feedback.documentCanceled"))
  }

  const handlePrint = async (documentId: number, printerConfigurationId: number) => {
    await fiscal.printDocument(documentId, printerConfigurationId)

    setPrintDocument(null)

    setMessage(t("feedback.successTitle"), t("feedback.printQueued"))
  }

  return (
    <div className="flex h-[calc(100%-25px)] w-full flex-col overflow-y-auto p-2 sm:p-4">
      <Animate type={AnimationTypes.fadeInTraslate} duration="1.1s">
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 className="flex items-center gap-2 text-xl font-black text-slate-900">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-orange-100 text-orange-700">
              <FontAwesomeIcon icon={faReceipt} />
            </span>

            {t("title")}
          </h2>

          <PopupHelpButton
            variant="global"
            helpKey="pages.fiscal-integration"
            title={t("title")}
            description={t("subtitle")}
            bullets={t("helpPopup:global.fiscalIntegration.bullets", { returnObjects: true }) as string[]}
            steps={t("helpPopup:global.fiscalIntegration.steps", { returnObjects: true }) as string[]}
          />
        </div>
      </Animate>

      {!settings.isEnabled && (
        <section className="mb-4 flex gap-3 rounded-xl border-2 border-amber-200 bg-amber-50 p-4 text-amber-900">
          <FontAwesomeIcon icon={faTriangleExclamation} className="mt-1" />

          <div>
            <strong className="text-sm">{t("disabled.title")}</strong>

            <p className="mt-1 text-xs leading-relaxed">{t("disabled.description")}</p>
          </div>
        </section>
      )}

      {latestRejectedDocument && (
        <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
          <section
            className="mb-4 flex flex-col gap-3 rounded-xl border-2 border-red-300 bg-red-50 p-4 text-red-950 shadow-sm sm:flex-row sm:items-center sm:justify-between"
            role="alert"
            aria-live="assertive"
          >
            <div className="flex min-w-0 gap-3">
              <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-600 text-white">
                <FontAwesomeIcon icon={faTriangleExclamation} />
              </span>

              <div className="min-w-0">
                <strong className="text-sm">{t("failureAlert.title", { count: rejectedDocuments.length })}</strong>

                <p className="mt-1 text-xs font-semibold leading-relaxed">
                  {t("failureAlert.order", { id: latestRejectedDocument.orderEntityId })}:{" "}
                  {latestRejectedDocument.message ?? t("documents.failureUnknown")}
                </p>

                <p className="mt-1 text-[11px] text-red-800">{t("failureAlert.description")}</p>
              </div>
            </div>

            <button
              type="button"
              onClick={openDocuments}
              className="inline-flex min-h-10 shrink-0 items-center justify-center gap-2 rounded-xl bg-red-700 px-4 py-2 text-xs font-bold text-white shadow-sm hover:bg-red-800"
              data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.fiscalIntegrationPage.button.openDocuments"
            >
              <FontAwesomeIcon icon={faClockRotateLeft} />
              {t("failureAlert.openDocuments")}
            </button>
          </section>
        </Animate>
      )}

      <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <section className="rounded-xl border-2 border-slate-200 bg-white p-4 shadow-sm">
          <span className="text-xs font-bold uppercase text-slate-500">{t("summary.integration")}</span>

          <strong className={`mt-2 block text-lg ${settings.isEnabled ? "text-emerald-700" : "text-amber-700"}`}>
            {settings.isEnabled ? t("summary.enabled") : t("summary.disabled")}
          </strong>
        </section>

        <section className="rounded-xl border-2 border-slate-200 bg-white p-4 shadow-sm">
          <span className="text-xs font-bold uppercase text-slate-500">{t("summary.issuer")}</span>

          <strong
            className={`mt-2 block text-lg ${settings.issuer.isConfigured ? "text-emerald-700" : "text-red-700"}`}
          >
            {settings.issuer.isConfigured ? t("summary.configured") : t("summary.pending")}
          </strong>
        </section>

        <section className="rounded-xl border-2 border-slate-200 bg-white p-4 shadow-sm">
          <span className="text-xs font-bold uppercase text-slate-500">{t("summary.environment")}</span>

          <strong className="mt-2 block text-lg text-slate-800">
            {settings.environment === EFiscalEnvironment.Production
              ? t("environment.production")
              : t("environment.homologation")}
          </strong>
        </section>

        <section className="rounded-xl border-2 border-slate-200 bg-white p-4 shadow-sm">
          <span className="text-xs font-bold uppercase text-slate-500">{t("summary.products")}</span>

          <strong className="mt-2 block text-lg text-slate-800">
            {configuredProducts}/{products.length}
          </strong>
        </section>

        <section className="rounded-xl border-2 border-slate-200 bg-white p-4 shadow-sm">
          <span className="text-xs font-bold uppercase text-slate-500">{t("summary.authorized")}</span>

          <strong className="mt-2 block text-lg text-emerald-700">{authorizedDocuments}</strong>
        </section>
      </div>

      <div className="mb-4 grid gap-2 sm:grid-cols-2 xl:flex xl:flex-wrap">
        <button
          type="button"
          onClick={openSettings}
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-orange-600 px-4 py-2 text-sm font-bold text-white shadow-sm hover:bg-orange-700"
          data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.fiscalIntegrationPage.button.openSettings"
        >
          <FontAwesomeIcon icon={faGear} />
          {t("actions.configure")}
        </button>

        <button
          type="button"
          onClick={openIssuer}
          className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border-2 px-4 py-2 text-sm font-bold shadow-sm ${
            settings.issuer.isConfigured
              ? "border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100"
              : "border-red-300 bg-red-50 text-red-800 hover:bg-red-100"
          }`}
          data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.fiscalIntegrationPage.button.openIssuer"
        >
          <FontAwesomeIcon icon={faBuilding} />
          {settings.issuer.isConfigured ? t("actions.issuer") : t("actions.configureIssuer")}
        </button>

        <button
          type="button"
          onClick={openManualEmission}
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white shadow-sm hover:bg-emerald-700"
          data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.fiscalIntegrationPage.button.openManualEmission"
        >
          <FontAwesomeIcon icon={faFileCirclePlus} />
          {t("actions.manualEmission")}
        </button>

        <button
          type="button"
          onClick={openDocuments}
          className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border-2 px-4 py-2 text-sm font-bold shadow-sm ${
            rejectedDocuments.length > 0
              ? "border-red-300 bg-red-50 text-red-800 hover:bg-red-100"
              : "border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
          }`}
          data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.fiscalIntegrationPage.button.openDocuments2"
        >
          <FontAwesomeIcon icon={faClockRotateLeft} />
          {t("actions.documents")}
          <span
            className={`rounded-full px-2 py-0.5 text-[11px] ${
              rejectedDocuments.length > 0 ? "bg-red-200 text-red-900" : "bg-indigo-100 text-indigo-800"
            }`}
          >
            {rejectedDocuments.length > 0 ? rejectedDocuments.length : documents.length}
          </span>
        </button>
        <a
          href={settings.affiliateUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border-2 border-sky-200 bg-sky-50 px-4 py-2 text-sm font-bold text-sky-700 shadow-sm hover:bg-sky-100"
          data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.fiscalIntegrationPage.link.createFocusAccount"
        >
          <FontAwesomeIcon icon={faLink} />
          {t("actions.createFocusAccount")}
        </a>
        <button
          type="button"
          onClick={() => void load()}
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border-2 border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm hover:bg-slate-50"
          data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.fiscalIntegrationPage.button.voidLoad"
        >
          <FontAwesomeIcon icon={faRotate} />
          {t("actions.refresh")}
        </button>
      </div>

      <section
        ref={productsSectionRef}
        className="flex min-h-[560px] min-w-0 flex-1 scroll-mt-4 flex-col rounded-2xl border-2 border-slate-200 bg-white p-3 shadow-sm sm:p-4"
      >
        <div className="flex flex-col gap-3 border-b border-slate-200 pb-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-black text-slate-900">{t("products.title")}</h3>

              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600">
                {t("products.total", { count: products.length })}
              </span>
            </div>

            <p className="mt-1 text-xs text-slate-500">{t("products.description")}</p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <button
              type="button"
              role="switch"
              aria-checked={allProductsSelected}
              onClick={toggleAllProducts}
              disabled={products.length === 0}
              className="inline-flex min-h-10 items-center justify-between gap-3 rounded-xl border-2 border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:border-sky-300 hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50 sm:justify-center"
              title={allProductsSelected ? t("products.clearSelection") : t("products.selectAll")}
              data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.fiscalIntegrationPage.button.clearSelection"
            >
              <span>{allProductsSelected ? t("products.clearSelection") : t("products.selectAll")}</span>
              <span
                className={`relative h-5 w-10 shrink-0 rounded-full transition-colors ${
                  allProductsSelected ? "bg-sky-600" : "bg-slate-300"
                }`}
              >
                <span
                  className={`absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                    allProductsSelected ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </span>
            </button>

            <button
              type="button"
              disabled={selectedProductIds.size === 0}
              onClick={openBulk}
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-sky-600 px-4 py-2 text-xs font-bold text-white shadow-sm hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-300"
              data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.fiscalIntegrationPage.button.openBulk"
            >
              <FontAwesomeIcon icon={faPenToSquare} />
              {t("products.applySelected", { count: selectedProductIds.size })}
            </button>
          </div>
        </div>

        <div className="mt-4 grid gap-3 lg:grid-cols-[minmax(280px,1fr)_auto] lg:items-center">
          <InputField
            type="text"
            id="Find-item"
            value={search}
            placeholder={t("products.search")}
            onChange={(event) => {
              setSearch(event.target.value)
              setCurrentPage(1)
            }}
            descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.fiscalIntegrationPage.input.search"
          />

          <span className="text-right text-xs font-bold text-slate-500">
            {t("products.selected", { count: selectedProductIds.size })}
          </span>
        </div>

        <div className="mt-3 hidden grid-cols-[56px_minmax(220px,1.5fr)_minmax(140px,0.7fr)_minmax(210px,1fr)_150px_120px] items-center gap-3 rounded-xl bg-slate-100 px-3 py-2 text-[11px] font-black uppercase tracking-wide text-slate-500 lg:grid">
          <span className="text-center">{t("products.columns.select")}</span>

          <span>{t("products.columns.product")}</span>

          <span>{t("products.columns.code")}</span>

          <span>{t("products.columns.taxation")}</span>

          <span>{t("products.columns.status")}</span>

          <span className="text-right">{t("products.columns.actions")}</span>
        </div>

        <div
          ref={productsListRef}
          className="mt-2 min-h-0 min-w-0 w-full max-w-full flex-1 space-y-2 overflow-y-auto overflow-x-hidden overscroll-contain pr-1"
        >
          {filteredProducts.length === 0 && (
            <div className="flex min-h-[260px] items-center justify-center rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
              {t("products.empty")}
            </div>
          )}

          {paginatedProducts.map((product) => {
            const selected = selectedProductIds.has(product.catalogProductEntityId)
            const selectionLabel = selected
              ? t("products.deselectProduct", { name: product.productName })
              : t("products.selectProduct", { name: product.productName })

            return (
              <article
                key={product.catalogProductEntityId}
                className={`grid items-center gap-3 rounded-xl border-2 p-3 transition-colors lg:grid-cols-[56px_minmax(220px,1.5fr)_minmax(140px,0.7fr)_minmax(210px,1fr)_150px_120px] ${
                  selected ? "border-sky-200 bg-sky-50/70" : "border-slate-100 bg-slate-50 hover:border-slate-200"
                }`}
              >
                <div className="flex items-center justify-between lg:justify-center">
                  <span className="text-xs font-bold text-slate-500 lg:hidden">{t("products.columns.select")}</span>

                  <button
                    type="button"
                    role="switch"
                    aria-checked={selected}
                    aria-label={selectionLabel}
                    onClick={() => toggleProduct(product.catalogProductEntityId)}
                    className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
                      selected ? "bg-sky-600" : "bg-slate-300 hover:bg-slate-400"
                    }`}
                    title={selectionLabel}
                    data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.fiscalIntegrationPage.button.selectionLabel"
                  >
                    <span
                      className={`absolute left-1 top-1 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                        selected ? "translate-x-5" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>

                <div className="min-w-0">
                  <strong className="block truncate text-sm text-slate-900">{product.productName}</strong>

                  <span className="mt-0.5 block text-[11px] text-slate-500">
                    {product.isConfigured ? t("products.readyHint") : t("products.pendingHint")}
                  </span>
                </div>

                <div className="min-w-0">
                  <span className="mb-0.5 block text-[10px] font-black uppercase text-slate-400 lg:hidden">
                    {t("products.columns.code")}
                  </span>

                  <span className="block truncate text-xs font-semibold text-slate-600">
                    {product.productCode || t("products.codeMissing")}
                  </span>
                </div>

                <div className="min-w-0">
                  <span className="mb-0.5 block text-[10px] font-black uppercase text-slate-400 lg:hidden">
                    {t("products.columns.taxation")}
                  </span>

                  <span className="block truncate text-xs font-semibold text-slate-700">
                    {product.isConfigured
                      ? t("products.configured", { ncm: product.ncm, cfop: product.cfop })
                      : t("products.pending")}
                  </span>
                </div>

                <div>
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold ${
                      product.isConfigured
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : "border-amber-200 bg-amber-50 text-amber-700"
                    }`}
                  >
                    <FontAwesomeIcon icon={product.isConfigured ? faCircleCheck : faCircleExclamation} />

                    {product.isConfigured ? t("products.statusConfigured") : t("products.statusPending")}
                  </span>
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => openProduct(product)}
                    className="inline-flex min-h-9 items-center justify-center gap-2 rounded-xl border-2 border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 shadow-sm hover:border-orange-300 hover:bg-orange-50 hover:text-orange-700"
                    title={t("products.edit")}
                    data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.fiscalIntegrationPage.button.edit"
                  >
                    <FontAwesomeIcon icon={faPenToSquare} />
                    {t("products.editAction")}
                  </button>
                </div>
              </article>
            )
          })}
        </div>

        <nav
          className="mt-3 flex flex-col gap-2 border-t border-slate-200 pt-3 sm:flex-row sm:items-center sm:justify-between"
          aria-label={t("products.pagination.ariaLabel")}
        >
          <span className="text-center text-xs font-semibold text-slate-500 sm:text-left">
            {t("products.pagination.range", {
              start: firstVisibleProduct,
              end: lastVisibleProduct,
              total: filteredProducts.length,
            })}
          </span>

          <div className="flex items-center justify-center gap-2">
            <button
              type="button"
              disabled={visiblePage === 1}
              onClick={() => changePage(visiblePage - 1)}
              className="inline-flex min-h-9 items-center gap-2 rounded-xl border-2 border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:border-orange-300 hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-40"
              data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.fiscalIntegrationPage.button.changePageVisiblePage1"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
              {t("products.pagination.previous")}
            </button>

            <span className="min-w-24 text-center text-xs font-black text-slate-700" aria-live="polite">
              {t("products.pagination.page", { current: visiblePage, total: totalPages })}
            </span>

            <button
              type="button"
              disabled={visiblePage === totalPages}
              onClick={() => changePage(visiblePage + 1)}
              className="inline-flex min-h-9 items-center gap-2 rounded-xl border-2 border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:border-orange-300 hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-40"
              data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.fiscalIntegrationPage.button.changePageVisiblePage12"
            >
              {t("products.pagination.next")}
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </nav>
      </section>

      <FiscalSettingsPopup
        isOpen={settingsOpen}
        settings={settingsDraft}
        onChange={setSettingsDraft}
        onClose={() => setSettingsOpen(false)}
        onSave={() => void handleSaveSettings()}
        onTest={() => void handleTest()}
        issuerConfigured={settings.issuer.isConfigured}
        onConfigureIssuer={() => {
          setSettingsOpen(false)
          openIssuer()
        }}
      />

      <FiscalIssuerSettingsPopup
        isOpen={issuerOpen}
        issuer={issuerDraft}
        onChange={setIssuerDraft}
        onClose={() => setIssuerOpen(false)}
        onSave={() => void handleSaveIssuer()}
      />

      <FiscalManualEmissionPopup
        isOpen={manualEmissionOpen}
        isEnabled={settings.isEnabled && settings.issuer.isConfigured}
        orderId={orderId}
        consumerCpf={consumerCpf}
        paymentType={manualPaymentType}
        requiresPaymentMethod={manualRequiresPaymentMethod}
        isOrderChecking={isManualOrderChecking}
        onChange={setOrderId}
        onConsumerCpfChange={setConsumerCpf}
        onPaymentTypeChange={setManualPaymentType}
        onClose={() => {
          setManualEmissionOpen(false)
          setConsumerCpf("")
          setManualPaymentType(EPaymentType.None)
          setManualRequiresPaymentMethod(false)
        }}
        onEmit={() => void handleEmit()}
      />

      <FiscalDocumentsPopup
        isOpen={documentsOpen}
        documents={documentSearchResult.items}
        pageNumber={documentSearchResult.pageNumber}
        pageSize={documentSearchResult.pageSize}
        totalCount={documentSearchResult.totalCount}
        hasNextPage={documentSearchResult.hasNextPage}
        onClose={() => setDocumentsOpen(false)}
        onSearch={searchDocuments}
        onRefresh={(documentId) => void handleRefresh(documentId)}
        onRetry={(documentId) => void handleRetry(documentId)}
        onOpenDanfce={(document) => fiscal.openDanfce(document)}
        onDownloadXml={(document) => void fiscal.downloadXml(document)}
        onPrint={setPrintDocument}
        onCancel={setCancelDocument}
      />

      <FiscalDocumentPrintPopup
        document={printDocument}
        onClose={() => setPrintDocument(null)}
        onConfirm={handlePrint}
      />

      <FiscalProductProfilePopup
        isOpen={profileOpen}
        profile={profileDraft}
        isBulk={bulkMode}
        taxRegime={settings.taxRegime}
        onChange={setProfileDraft}
        onClose={() => setProfileOpen(false)}
        onSave={() => void handleSaveProfile()}
      />

      <FiscalCancelPopup
        key={cancelDocument?.id ?? "closed"}
        isOpen={cancelDocument !== null}
        onClose={() => setCancelDocument(null)}
        onConfirm={(reason) => void handleCancel(reason)}
      />
    </div>
  )
}
